import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Users, 
  CalendarDays, 
  Newspaper, 
  TrendingUp,
  TrendingDown,
  Eye,
  Gamepad2,
  Activity,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { statsAPI } from '../../services/api';
import DiscordConfig from '../DiscordConfig';
import ExportCard from './ExportCard';

// Line chart component with comparison (two curves)
const LineChart = ({ data, previousData, currentLabel, previousLabel }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        Aucune donnée disponible
      </div>
    );
  }

  // Find max value across both datasets for consistent scaling
  const allVisits = [
    ...data.map(d => d.visits),
    ...(previousData || []).map(d => d.visits)
  ];
  const maxValue = Math.max(...allVisits, 1);
  const chartHeight = 200;
  const padding = 30;
  
  // Calculate points for the current line
  const currentPoints = data.map((item, index) => {
    const x = data.length > 1 ? (index / (data.length - 1)) * 100 : 50;
    const y = chartHeight - padding - (item.visits / maxValue) * (chartHeight - padding * 2);
    return { x, y, ...item };
  });

  // Calculate points for the previous line
  const previousPoints = previousData ? previousData.map((item, index) => {
    const x = previousData.length > 1 ? (index / (previousData.length - 1)) * 100 : 50;
    const y = chartHeight - padding - (item.visits / maxValue) * (chartHeight - padding * 2);
    return { x, y, ...item };
  }) : [];

  // Create SVG path
  const createPath = (points) => {
    if (points.length < 2) return points.length === 1 ? `M ${points[0].x} ${points[0].y}` : '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${points[i].y}`;
    }
    return path;
  };

  // Create area path (filled area under curve)
  const createAreaPath = (points) => {
    if (points.length < 2) return '';
    
    const bottomY = chartHeight - padding;
    let path = `M ${points[0].x} ${bottomY}`;
    path += ` L ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${points[i].y}`;
    }
    
    path += ` L ${points[points.length - 1].x} ${bottomY}`;
    path += ' Z';
    
    return path;
  };

  return (
    <div className="relative">
      {/* Legend */}
      <div className="flex items-center justify-end gap-4 mb-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-gradient-to-r from-purple-600 to-purple-400"></div>
          <span className="text-purple-300">{currentLabel || 'Période actuelle'}</span>
        </div>
        {previousData && previousData.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 border-t-2 border-dashed border-gray-500"></div>
            <span className="text-gray-400">{previousLabel || 'Période précédente'}</span>
          </div>
        )}
      </div>

      {/* SVG Chart */}
      <svg 
        viewBox={`0 0 100 ${chartHeight}`} 
        className="w-full h-56"
        preserveAspectRatio="none"
      >
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((percent) => (
          <line
            key={percent}
            x1="0"
            y1={chartHeight - padding - (percent / 100) * (chartHeight - padding * 2)}
            x2="100"
            y2={chartHeight - padding - (percent / 100) * (chartHeight - padding * 2)}
            stroke="rgba(147, 51, 234, 0.15)"
            strokeWidth="0.2"
          />
        ))}
        
        {/* Previous period area fill (subtle) */}
        {previousPoints.length > 1 && (
          <path
            d={createAreaPath(previousPoints)}
            fill="rgba(100, 100, 100, 0.1)"
          />
        )}
        
        {/* Current period area fill */}
        <path
          d={createAreaPath(currentPoints)}
          fill="url(#areaGradient)"
          opacity="0.4"
        />
        
        {/* Previous period line (dashed) */}
        {previousPoints.length > 0 && (
          <path
            d={createPath(previousPoints)}
            fill="none"
            stroke="rgba(156, 163, 175, 0.6)"
            strokeWidth="0.6"
            strokeDasharray="2,2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        
        {/* Current period line */}
        <path
          d={createPath(currentPoints)}
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth="0.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Previous period data points */}
        {previousPoints.map((point, index) => (
          <circle
            key={`prev-${index}`}
            cx={point.x}
            cy={point.y}
            r="0.8"
            fill="rgba(156, 163, 175, 0.6)"
            className="hover:r-1.5 transition-all"
          />
        ))}
        
        {/* Current period data points */}
        {currentPoints.map((point, index) => (
          <circle
            key={`curr-${index}`}
            cx={point.x}
            cy={point.y}
            r="1"
            fill="#a855f7"
            stroke="#fff"
            strokeWidth="0.3"
            className="hover:r-2 transition-all cursor-pointer"
          />
        ))}
        
        {/* Gradients */}
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* X-axis labels */}
      <div className="flex justify-between px-1 mt-2 border-t border-purple-500/20 pt-2">
        {data.map((item, index) => {
          // Show fewer labels if there are many data points
          const showLabel = data.length <= 12 || index % Math.ceil(data.length / 12) === 0;
          const prevValue = previousData && previousData[index] ? previousData[index].visits : null;
          
          return (
            <div 
              key={index} 
              className="flex-1 text-center relative group"
              style={{ maxWidth: `${100 / data.length}%` }}
            >
              {showLabel && (
                <span className="text-xs text-gray-400 font-medium">{item.label}</span>
              )}
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/95 text-xs px-3 py-2 rounded-lg whitespace-nowrap z-10 border border-purple-500/30">
                <div className="text-purple-300 font-bold">{item.label}</div>
                <div className="text-purple-400">Actuel: {item.visits}</div>
                {prevValue !== null && (
                  <div className="text-gray-400">Précédent: {prevValue}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = "purple" }) => (
  <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-5">
    <div className="flex items-start justify-between">
      <div className={`p-2.5 bg-${color}-600/30 rounded-lg`}>
        <Icon className={`h-5 w-5 text-${color}-400`} />
      </div>
    </div>
    <div className="mt-3">
      <p className="text-2xl sm:text-3xl font-bold text-white">{value}</p>
      <p className="text-gray-400 text-sm mt-1">{label}</p>
      {subValue && <p className="text-purple-400 text-xs mt-0.5">{subValue}</p>}
    </div>
  </Card>
);

const StatsTab = ({ stats: initialStats }) => {
  const [period, setPeriod] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - now.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [visitsData, setVisitsData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(initialStats);

  // Period options
  const periods = [
    { value: 'day', label: 'Jour' },
    { value: 'week', label: 'Semaine' },
    { value: 'month', label: 'Mois' }
  ];

  // Load main stats if not provided
  useEffect(() => {
    if (!stats) {
      loadMainStats();
    }
    loadActivityData();
  }, []);

  // Update stats when initialStats changes
  useEffect(() => {
    if (initialStats) {
      setStats(initialStats);
    }
  }, [initialStats]);

  // Load visits data when period or dates change
  useEffect(() => {
    loadVisitsData();
  }, [period, selectedDate, selectedWeekStart, selectedMonth]);

  const loadMainStats = async () => {
    try {
      const data = await statsAPI.get();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadVisitsData = async () => {
    try {
      setLoading(true);
      let params = { period };
      
      if (period === 'day') {
        params.date = selectedDate;
      } else if (period === 'week') {
        params.week_start = selectedWeekStart;
      } else if (period === 'month') {
        params.month = selectedMonth;
      }
      
      const data = await statsAPI.getVisits(params);
      setVisitsData(data);
    } catch (error) {
      console.error('Error loading visits data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityData = async () => {
    try {
      const data = await statsAPI.getActivity();
      setActivityData(data);
    } catch (error) {
      console.error('Error loading activity data:', error);
    }
  };

  // Navigation helpers
  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const navigateWeek = (direction) => {
    const date = new Date(selectedWeekStart);
    date.setDate(date.getDate() + (direction * 7));
    setSelectedWeekStart(date.toISOString().split('T')[0]);
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + direction, 1);
    setSelectedMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
  };

  if (!stats) {
    return (
      <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
          <span className="ml-3 text-purple-300">Chargement des statistiques...</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard 
          icon={Users} 
          label="Membres actifs" 
          value={stats.users?.members || 0}
          subValue={stats.users?.new_this_month > 0 ? `+${stats.users.new_this_month} ce mois` : null}
          color="purple"
        />
        <StatCard 
          icon={CalendarDays} 
          label="Événements à venir" 
          value={stats.events?.upcoming || 0}
          subValue={stats.events?.participants > 0 ? `${stats.events.participants} inscrits` : null}
          color="blue"
        />
        <StatCard 
          icon={Newspaper} 
          label="Articles publiés" 
          value={stats.content?.news || 0}
          color="green"
        />
        <StatCard 
          icon={Gamepad2} 
          label="Jeux en ludothèque" 
          value={stats.content?.games || 0}
          color="pink"
        />
      </div>

      {/* Visits Chart */}
      <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-6">
        <div className="flex flex-col gap-4 mb-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-purple-300 flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Fréquentation du site
              </h3>
              {visitsData?.period_label && (
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-400">
                    {visitsData.period_label}
                  </span>
                  <span className="text-lg font-bold text-purple-300">
                    {visitsData.summary?.total_visits || 0} visites
                  </span>
                  {visitsData.summary?.change_percent !== undefined && (
                    <span className={`flex items-center gap-1 text-sm font-semibold ${
                      visitsData.summary.change_percent >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {visitsData.summary.change_percent >= 0 ? (
                        <TrendingUp className="h-4 w-4" />
                      ) : (
                        <TrendingDown className="h-4 w-4" />
                      )}
                      {visitsData.summary.change_percent >= 0 ? '+' : ''}{visitsData.summary.change_percent}%
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Period Selector */}
            <div className="flex gap-1.5">
              {periods.map(p => (
                <Button
                  key={p.value}
                  variant={period === p.value ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPeriod(p.value)}
                  className={period === p.value 
                    ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                    : 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/30'
                  }
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Date Selector based on period */}
          <div className="flex items-center justify-center gap-3 bg-black/30 rounded-lg p-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (period === 'day') navigateDate(-1);
                else if (period === 'week') navigateWeek(-1);
                else navigateMonth(-1);
              }}
              className="text-purple-400 hover:text-purple-300"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>

            {period === 'day' && (
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto bg-black/50 border-purple-500/30 text-white text-center [&::-webkit-calendar-picker-indicator]:invert"
              />
            )}

            {period === 'week' && (
              <Input
                type="date"
                value={selectedWeekStart}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const monday = new Date(date);
                  monday.setDate(date.getDate() - date.getDay() + 1);
                  setSelectedWeekStart(monday.toISOString().split('T')[0]);
                }}
                className="w-auto bg-black/50 border-purple-500/30 text-white text-center [&::-webkit-calendar-picker-indicator]:invert"
              />
            )}

            {period === 'month' && (
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-auto bg-black/50 border-purple-500/30 text-white text-center [&::-webkit-calendar-picker-indicator]:invert"
              />
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (period === 'day') navigateDate(1);
                else if (period === 'week') navigateWeek(1);
                else navigateMonth(1);
              }}
              className="text-purple-400 hover:text-purple-300"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Chart */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent"></div>
          </div>
        ) : (
          <LineChart 
            data={visitsData?.data || []} 
            previousData={visitsData?.previous_data || []}
            currentLabel={visitsData?.period_label}
            previousLabel={visitsData?.previous_label}
          />
        )}

        {/* Summary Stats */}
        {visitsData?.summary && (
          <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t border-purple-500/20">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{visitsData.summary.total_visits}</p>
              <p className="text-xs text-gray-400">Total actuel</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-500">{visitsData.summary.prev_total}</p>
              <p className="text-xs text-gray-400">Total précédent</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{visitsData.summary.avg_visits}</p>
              <p className="text-xs text-gray-400">Moyenne</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{visitsData.summary.max_visits}</p>
              <p className="text-xs text-gray-400">Pic max</p>
            </div>
          </div>
        )}
      </Card>

      {/* Activity Summary */}
      {activityData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Quick Stats */}
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-6">
            <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Activité récente
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                <span className="text-gray-300">Aujourd'hui</span>
                <span className="text-xl font-bold text-purple-300">{activityData.today?.visits || 0} visites</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                <span className="text-gray-300">Cette semaine</span>
                <div className="text-right">
                  <span className="text-xl font-bold text-purple-300">{activityData.week?.visits || 0} visites</span>
                  {activityData.week?.new_members > 0 && (
                    <p className="text-xs text-green-400">+{activityData.week.new_members} nouveaux membres</p>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                <span className="text-gray-300">Ce mois</span>
                <div className="text-right">
                  <span className="text-xl font-bold text-purple-300">{activityData.month?.visits || 0} visites</span>
                  {activityData.month?.new_members > 0 && (
                    <p className="text-xs text-green-400">+{activityData.month.new_members} nouveaux membres</p>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Top Pages */}
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-4 sm:p-6">
            <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Pages populaires (7j)
            </h3>
            <div className="space-y-2">
              {activityData.top_pages?.length > 0 ? (
                activityData.top_pages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-purple-400 font-bold text-lg">#{index + 1}</span>
                      <span className="text-gray-300">{page.page}</span>
                    </div>
                    <span className="text-purple-300 font-semibold">{page.visits}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">Aucune donnée</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* CSV Exports */}
      <ExportCard />

      {/* Discord Configuration - Kept as requested */}
      <DiscordConfig />
    </div>
  );
};

export default StatsTab;
